import {
  CertificateDefinition,
  SummaryFieldDef,
} from "../models/certificate.schema";

/**
 * Helper service for resolving certificate summary field values
 */
export class CertificateSummaryHelper {
  /**
   * Resolves all summary lines for a certificate based on its definition and data
   */
  static getSummaryLines(
    def: CertificateDefinition,
    data: Record<string, unknown>,
  ): { label: string; value: string }[] {
    // Find the summary field in the meta section
    const metaSection = def.sections.find((s) => s.type === "group" && s.meta);
    if (!metaSection) return [];

    const summaryField = (metaSection.fields as any[]).find(
      (f) => f.type === "summary",
    ) as SummaryFieldDef | undefined;
    if (!summaryField) return [];

    return summaryField.lines.map((line) => ({
      label: line.label,
      value: this.resolveValuePattern(line.valuePattern, data),
    }));
  }

  /**
   * Resolves a template string containing ${field.path} references against the certificate data
   */
  private static resolveValuePattern(
    pattern: string,
    data: Record<string, unknown>,
  ): string {
    // First, perform basic interpolation
    let result = pattern.replace(/\${([^}]+)}/g, (_, path) => {
      try {
        const value = path
          .split(".")
          .reduce((obj: any, key: string) => obj?.[key], data);
        return value?.toString() ?? "";
      } catch {
        return "";
      }
    });

    // Handle conditional separators: " /? " means include " / " only if both adjacent values are non-empty
    result = result.replace(/(\S+)\s*\/\?\s*(\S+)/g, (match, left, right) => {
      // If both left and right are non-empty, include the separator
      if (left.trim() && right.trim()) {
        return left + " / " + right;
      }
      // Otherwise, just concatenate without separator
      return left + right;
    });

    // Clean up extra whitespace and trim
    return result.replace(/\s+/g, ' ').trim();
  }
}
